import React, { memo, useCallback, useEffect, useRef } from "react";
import cx from "classnames";
import { css } from "@linaria/core";
import { colors, spacings } from "@ui/Theme";
import Text from "@ui/Text";
import { FragmentSlot, OptionV2 } from "@ui/Option/v2";
import Menu from "@ui/Menu";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { DeleteBinLine, FileTextLine, More2Fill } from "@ui/IconV2/react";
import t from "../../../../../../../../i18n";
import { useCurrentEditorBlock } from "../../../../../hook/context/gui";
import { type BlockDiagramModelT } from "../../../../../schema/block/diagram";
import { useDashboardEditorContext } from "../../../../../hook/context/editor";
import { AnnotationIcon } from "./AnnotationIcon";
import { useSelectedDiagramAnnotation } from "../../hook/useSelectedDiagramAnnotation";
import Ellipsis from "@ui/Ellipsis";
import { getAvailableResourceKind } from "../../../../../utils/getAvailableResourceKind";

import {
  AGGREGATE_NO_SYSTEM,
  AGGREGATE_UNEXPLAINED_LOSSES,
  useAvailableResources,
} from "../../../../../hook/useAvailableResources";
import { useTranslations, type Translation } from "../../../../../../../../api/useTranslations";

const getAnnotationDisplayName = ({
  annotationCustomName,
  annotationIdentifier,
  index,
  translations,
}: {
  annotationCustomName: string | null;
  annotationIdentifier: string | null;
  index: number;
  translations: Translation;
}) => {
  if (annotationCustomName && annotationCustomName.length > 0) {
    return annotationCustomName;
  }
  if (annotationIdentifier && annotationIdentifier.length > 0) {
    if (annotationIdentifier === AGGREGATE_UNEXPLAINED_LOSSES)
      return t("charts.legend.unexplained_losses", translations);
    if (annotationIdentifier === AGGREGATE_NO_SYSTEM)
      return t("project.dashboard.editor.gui.no_system");
    return annotationIdentifier;
  }
  return `Annotation ${index + 1}`;
};

export const Annotation = memo(
  ({
    projectId,
    annotation,
    kind,
    index,
    selected,
    onClick,
  }: {
    projectId: string;
    annotation: BlockDiagramModelT["config"]["layers"][number];
    index: number;
    kind: string | null;
    selected: boolean;
    onClick: () => void;
  }) => {
    const availableResources = useAvailableResources({ projectId });
    const annotationRef = useRef<HTMLAnchorElement>(null);
    const editor = useDashboardEditorContext();
    const block = useCurrentEditorBlock<BlockDiagramModelT>("DIAGRAM");
    const [, selectAnnotationIndex] = useSelectedDiagramAnnotation({
      scope: block?.id ?? "",
    });

    const selectedIdentifier = annotation.encodings[0]?.identifier ?? null;
    const displayName = availableResources.fault.identifiers.find(
      i => i.name === selectedIdentifier,
    )?.displayName;

    const deleteAnnotation = useCallback(async () => {
      if (!block) return;
      const previous = block.config.layers.length - 2;
      const identifier = annotation.encodings.map(e => e.identifier)[0];
      const kind = identifier ? getAvailableResourceKind(availableResources, identifier) : null;

      const updatedBlock = {
        ...block,
        data: {
          ...block.data,
          transforms:
            kind === "SYSTEM"
              ? block.data.transforms?.filter(t =>
                  identifier ? !t.filter?.some(f => f.value.includes(identifier)) : true,
                )
              : block.data.transforms,
        },
        config: {
          ...block.config,
          layers: block.config.layers.filter((_, i) => i !== index),
        },
      };
      await editor?.updateBlock(updatedBlock);
      selectAnnotationIndex(previous > -1 ? previous : null);
    }, [annotation.encodings, availableResources, block, editor, index, selectAnnotationIndex]);

    useEffect(() => {
      if (!annotationRef.current || !selected) return;
      const top = annotationRef.current.offsetTop;
      annotationRef.current.parentElement?.scrollTo({
        top,
        behavior: "smooth",
      });
    }, [selected]);

    const translations = useTranslations();

    const annotationCustomName =
      kind === "FAULT" ? (displayName ?? null) : (annotation.name ?? null);

    return (
      <OptionV2
        ref={annotationRef}
        selected={selected}
        className={cx(annotationItem)}
        onClick={onClick}
      >
        {annotation.position[2] === -1 && annotation.position[3] === -1 ? (
          <FragmentSlot slot="icon">
            <FileTextLine color={colors.carbon800} />
          </FragmentSlot>
        ) : (
          <AnnotationIcon
            width={annotation.position[2]}
            height={annotation.position[3]}
            slot="icon"
          />
        )}

        <Text
          slot="text"
          as="span"
          type={selected ? "body1SemiBold" : "body1"}
          className={annotationText}
        >
          <Ellipsis>
            {getAnnotationDisplayName({
              annotationCustomName,
              annotationIdentifier: annotation.encodings[0]?.identifier ?? null,
              index,
              translations,
            })}
          </Ellipsis>
        </Text>
        {kind && (
          <Text slot="description" as="span" type="body2" className={annotationType}>
            {t("project.dashboard.encoding.kind", kind)}
          </Text>
        )}
        <FragmentSlot slot="extra">
          <Menu
            overlay={
              <div>
                <OptionV2 onClick={deleteAnnotation} closeOnClick>
                  <FragmentSlot slot="icon">
                    <DeleteBinLine />
                  </FragmentSlot>
                  <Text type="body1" slot="text" className={annotationActionText}>
                    {t("project.dashboard.actions.delete")}
                  </Text>
                </OptionV2>
              </div>
            }
          >
            <ButtonIconV2>
              <More2Fill size="s" />
            </ButtonIconV2>
          </Menu>
        </FragmentSlot>
      </OptionV2>
    );
  },
);

const annotationItem = css`
  color: ${colors.carbon800};

  & + & {
    margin-top: ${spacings.xxs};
  }
`;

const annotationText = css`
  color: ${colors.carbon900};

  & > span {
    width: 100%;
    display: inline-flex;
  }
`;

const annotationType = css`
  color: ${colors.carbon800};
`;

const annotationActionText = css`
  color: ${colors.carbon900};
`;
